import { AlertTriangle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * The third state. Loading, empty and error are different answers, and a
 * failed fetch rendering as "you have no data" is how a page lies quietly.
 * So this is unmistakably not empty: the destructive colour, what failed,
 * and the one action that might fix it.
 */
export function ErrorState({
  title = "This page could not load",
  description = "The request failed. Try again, and if it keeps failing tell us what you were looking at.",
  onRetry,
  size = "page",
}: {
  title?: string;
  description?: string;
  onRetry?: () => void;
  /**
   * `page` fills a page or a pane; `compact` is the same state inside a card,
   * a section or a popover, sized as `EmptyState`'s compact is, so a failure
   * in a small surface does not shout louder than the surface itself.
   */
  size?: "page" | "compact";
}) {
  const compact = size === "compact";
  return (
    <div
      role="alert"
      className={cn(
        "flex flex-col items-center justify-center text-center",
        compact ? "gap-2.5 py-6" : "gap-4 py-20",
      )}
    >
      <div
        className={cn(
          "border-destructive/40 bg-destructive/10 flex items-center justify-center rounded-full border",
          compact ? "h-9 w-9" : "h-14 w-14",
        )}
      >
        <AlertTriangle
          className={cn("text-destructive", compact ? "h-4 w-4" : "h-6 w-6")}
          strokeWidth={1.5}
          aria-hidden
        />
      </div>
      <div className={compact ? "space-y-1" : "space-y-2"}>
        <h3
          className={cn(
            "text-foreground",
            compact ? "text-sm font-medium" : "text-base",
          )}
        >
          {title}
        </h3>
        <p className="text-muted-foreground max-w-sm text-sm leading-relaxed">
          {description}
        </p>
      </div>
      {onRetry && (
        <Button
          size="sm"
          variant="outline"
          className={compact ? undefined : "mt-1"}
          onClick={onRetry}
        >
          Try again
        </Button>
      )}
    </div>
  );
}
